import * as React from 'react';
import * as acl from 'common/utils/acl';
import * as qs from 'qs';

import * as dateAddDays from 'date-fns/add_days';
import * as dateFormat from 'date-fns/format';

import { EntityActions, EntityType } from 'common/entity';
import { FilterOption, Filters } from 'common/components/Filters';
import { Route, RouteComponentProps } from 'react-router-dom';
import { getFilterTerms, stripQueryTerms } from '../../common/utils/search';

import { AppState } from 'common/models/AppState';
import { Filter } from 'common/components/Filters';
import { Member } from 'common/api/models/Member';
import { OrgsDetail } from './OrgsDetail';
import { OrgsDialog } from './OrgsDialog';
import { OrgsTable } from './OrgsTable';
import { Page } from 'common/components/layout/Page/Page';
import { Search } from 'common/components/Search';
import { UserPermissions } from '../../common/api/models/UserPermissions';
import { getExportUrl } from 'common/utils/api';
import { Tag } from 'common/api/models/Tag';

export interface OrgsProps {
  appState: AppState;
  entityActions: EntityActions;
}
export interface OrgsState {
  error?: string;
  editOrg?: Member;
  isReady: boolean;
  savedAt: Date;
}

export const NEW_ORG: Member = {
  status: Member.Status.ACTIVE,
  workAddress: { country: 'US' },
  vars: { ceuProvider: false },
};

export class Orgs extends React.Component<OrgsProps & RouteComponentProps<{}>, OrgsState> {
  state: OrgsState = {
    isReady: false,
    savedAt: new Date(),
  };

  filters: Filter[] = [];
  filterNames = [
    'status',
    'member',
    'renewType',
    'expire',
    'type',
    'chapter',
    'ceu',
    'tag-internal',
    'staffCountRange',
    'signupSource',
    'accountManager',
  ];

  componentDidMount() {
    this.props.entityActions.fetchAll(EntityType.CHAPTER, () => {
      let chapterOptions: FilterOption[] = [{ label: 'None', value: '' }];
      for (let k in this.props.appState.entities.chapter) {
        if (this.props.appState.entities.chapter.hasOwnProperty(k)) {
          const chapter = this.props.appState.entities.chapter[k];
          chapterOptions.push({ label: chapter.name!, value: chapter.id! });
        }
      }
      let internalTagOptions: FilterOption[] = [{ label: 'All', value: '' }];
      for (let k in this.props.appState.entities.tag) {
        if (this.props.appState.entities.tag.hasOwnProperty(k)) {
          const tag = this.props.appState.entities.tag[k];
          if (tag.type === Tag.Type.INTERNAL) {
            internalTagOptions.push({ label: tag.name!, value: tag.slug! });
          }
        }
      }

      chapterOptions.sort((a, b) => (a.label > b.label ? 1 : -1));
      chapterOptions.unshift({ label: 'All', value: '' });
      this.filters = [
        {
          name: 'status',
          label: 'Status',
          options: [
            { label: 'All', value: '' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Expired', value: 'expired' },
            { label: 'Active', value: 'active' },
          ],
        },
        {
          name: 'member',
          label: 'Member Type',
          options: [
            { label: 'All', value: '' },
            { label: 'NONE', value: 'NONE' },
            { label: 'MEMB', value: 'MEMB' },
            { label: 'SCHOOL', value: 'SCHOOL' },
            { label: 'IP', value: 'IP' },
            { label: 'IBRANCH', value: 'IBRANCH' },
            { label: 'BRANCH', value: 'BRANCH' },
          ],
        },
        {
          name: 'renewType',
          label: 'Renewal Type',
          options: [
            { label: 'All', value: '' },
            { label: 'Invoice', value: 'BILL' },
            { label: 'Auto-Renew', value: 'AUTO' },
            { label: 'Manual', value: 'MANUAL' },
          ],
        },
        {
          name: 'expire',
          label: 'Expires',
          options: [
            { label: 'All', value: '' },
            {
              label: 'Past 120 Days',
              value:
                dateFormat(dateAddDays(new Date(), -120), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
            },
            {
              label: 'Past 90 Days',
              value:
                dateFormat(dateAddDays(new Date(), -90), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
            },
            {
              label: 'Past 60 Days',
              value:
                dateFormat(dateAddDays(new Date(), -60), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
            },
            {
              label: 'Past 30 Days',
              value:
                dateFormat(dateAddDays(new Date(), -30), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
            },
            {
              label: 'Past 7 Days',
              value: dateFormat(dateAddDays(new Date(), -7), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
            },
            {
              label: 'Next 7 Days',
              value: dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 7), 'YYYY-MM-DD'),
            },
            {
              label: 'Next 30 Days',
              value: dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 30), 'YYYY-MM-DD'),
            },
            {
              label: 'Next 60 Days',
              value: dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 60), 'YYYY-MM-DD'),
            },
            {
              label: 'Next 90 Days',
              value: dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 90), 'YYYY-MM-DD'),
            },
            {
              label: 'Next 120 Days',
              value:
                dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 120), 'YYYY-MM-DD'),
            },
          ],
        },
        { name: 'chapter', label: 'Chapter', options: chapterOptions },
        {
          name: 'ceu',
          label: 'CEU Provider',
          options: [{ label: 'All', value: '' }, { label: 'No', value: '0' }, { label: 'Yes', value: '1' }],
        },
        {
          name: 'tag-internal',
          label: 'Internal Tags',
          options: internalTagOptions,
        },
        {
          name: 'staffCountRange',
          label: 'Staff Count Range',
          options: [
            { label: 'All', value: '' },
            { label: '1', value: 'MAX_1' },
            { label: '2-10', value: 'MAX_10' },
            { label: '11-50', value: 'MAX_50' },
            { label: '51-200', value: 'MAX_200' },
            { label: '201-500', value: 'MAX_500' },
            { label: '501-1,000', value: 'MAX_1000' },
            { label: '1,001-5,000', value: 'MAX_5000' },
            { label: '5,001-10,000', value: 'MAX_10000' },
            { label: '10,000+', value: 'OVER_10000' },
          ],
        },
        {
          name: 'signupSource',
          label: 'Signup Source',
          options: [
            { label: 'All', value: '' },
            { label: 'Online', value: 'ONLINE' },
            { label: 'Chapter', value: 'CHAPTER' },
            { label: 'Sales', value: 'SALES' },
            { label: 'Reactivation', value: 'REACTIVATION' },
            { label: 'Event', value: 'EVENT' },
          ],
        },
        {
          name: 'accountManager',
          label: 'Account Manager',
          options: [
            { label: 'All', value: '' },
            { label: 'Andreia Soares', value: '"Andreia Soares"' },
            { label: 'Blair Loftspring', value: '"Blair Loftspring"' },
            { label: 'Jennifer Chick-Gray', value: '"Jennifer Chick-Gray"' },
            { label: 'Marcel Blachowski', value: '"Marcel Blachowski"' },
            { label: 'Nancy Bove', value: '"Nancy Bove"' },
            { label: 'Regina Vento', value: '"Regina Vento"' },
          ],
        },
      ];
      this.setState({ isReady: true });
    });
  }

  handleQuery = (query: string) => {
    if (!query) {
      this.props.history.push(this.props.match.url);
    } else {
      this.props.history.push(this.props.match.url + '?' + qs.stringify({ q: query }));
    }
  };

  getQuery() {
    const q = qs.parse(this.props.location.search.substring(1)).q;
    return q !== undefined ? q : '';
  }

  handleSubmitSearch = (value: string) => {
    const query = this.getQuery();
    const filterValue = getFilterTerms(query, this.filterNames);
    const newSearchQuery = (value + ' ' + filterValue).trim();
    this.handleQuery(newSearchQuery);
  };

  handleAdd = () => {
    this.setState({ editOrg: NEW_ORG });
  };

  handleEdit = (org: Member) => {
    this.setState({ editOrg: org });
  };

  handleClickRow = (num: string) => {
    this.props.history.push('/orgs/' + num);
  };

  handleCancelDialog = () => {
    this.setState({ editOrg: undefined });
  };

  handleSaveDialog = (id: string) => {
    this.props.entityActions.refetch(EntityType.MEMBER, id);
    this.setState({ editOrg: undefined, savedAt: new Date() });
  };

  render() {
    if (!this.state.isReady) {
      return null;
    }
    const query = this.getQuery();
    const searchQuery = stripQueryTerms(query, this.filterNames);
    const exportCsvUrl = getExportUrl('/v2/member/export', { q: query + ' type:org' });
    return (
      <div>
        {this.state.error && (
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {this.state.error}
          </div>
        )}
        <Route
          path="/orgs"
          exact={true}
          render={props => (
            <Page
              title="Organizations"
              primaryAction={{
                shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
                onClick: this.handleAdd,
                label: 'Add Org',
              }}
              secondaryActions={[
                {
                  shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.EXPORT),
                  href: exportCsvUrl,
                  iconName: 'download',
                  label: 'Export CSV',
                },
              ]}
            >
              <div className="card card-responsive">
                <div className="p-3 p-sm-4">
                  <div className="mb-3">
                    <Search
                      placeholder="Search by name or email"
                      onSubmit={this.handleSubmitSearch}
                      initialValue={searchQuery}
                      pathToClearSearch="/orgs?q=status:active"
                    />
                  </div>
                  <div className="mb-3">
                    <Filters value={query} filters={this.filters} onChange={this.handleQuery} />
                    <OrgsTable
                      appState={this.props.appState}
                      entityActions={this.props.entityActions}
                      search={query}
                      onClick={this.handleClickRow}
                    />
                  </div>
                </div>
              </div>
            </Page>
          )}
        />
        <Route
          path="/orgs/:number"
          render={props => (
            <OrgsDetail
              appState={this.props.appState}
              entityActions={this.props.entityActions}
              number={props.match.params.number}
              onEdit={this.handleEdit}
              reloadAt={this.state.savedAt}
              {...this.props}
            />
          )}
        />

        <OrgsDialog
          org={this.state.editOrg}
          appState={this.props.appState}
          onSave={this.handleSaveDialog}
          onCancel={this.handleCancelDialog}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/Orgs.tsx